
import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Circle, PlayCircle, User, Edit2, Trash2 } from "lucide-react";
import { ProjectSubtask } from '../types';

interface SubtasksSummaryViewProps {
  subtasks: ProjectSubtask[];
  selectedTaskId: string | null;
  isLoading: boolean;
  onEdit: (subtask: ProjectSubtask) => void;
  onDelete: (subtaskId: string) => void;
  onStatusChange: (subtaskId: string, status: 'To Do' | 'In Progress' | 'Done') => void;
  isRestrictedUser?: boolean;
}

const SubtasksSummaryView = ({ 
  subtasks, 
  selectedTaskId, 
  isLoading, 
  onEdit,
  onDelete,
  onStatusChange,
  isRestrictedUser = false
}: SubtasksSummaryViewProps) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Done':
        return 'bg-green-500/20 text-green-300 border-green-500/30 hover:bg-green-500/30';
      case 'In Progress':
        return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30 hover:bg-yellow-500/30';
      default:
        return 'bg-gray-500/20 text-gray-300 border-gray-500/30 hover:bg-gray-500/30';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Done':
        return <CheckCircle2 className="h-4 w-4" />;
      case 'In Progress':
        return <PlayCircle className="h-4 w-4" />;
      default:
        return <Circle className="h-4 w-4" />;
    }
  };

  const getNextStatus = (currentStatus: string) => {
    switch (currentStatus) {
      case 'To Do':
        return 'In Progress';
      case 'In Progress':
        return 'Done';
      case 'Done':
        return 'To Do';
      default:
        return 'To Do';
    }
  };

  const getCardGradient = (status: string) => {
    switch (status) {
      case 'Done':
        return 'bg-gradient-to-br from-green-500/20 via-green-400/10 to-emerald-500/20';
      case 'In Progress':
        return 'bg-gradient-to-br from-yellow-500/20 via-orange-400/10 to-amber-500/20';
      default:
        return 'bg-gradient-to-br from-gray-500/20 via-slate-400/10 to-gray-600/20';
    }
  };

  if (!selectedTaskId) {
    return (
      <div className="p-6 text-center">
        <div className="text-gray-300 text-sm mb-3">
          Select a task from the progress overview to view and manage its subtasks.
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-6 text-center">
        <div className="text-gray-300 text-sm">Loading subtasks...</div>
      </div>
    );
  }

  if (subtasks.length === 0) {
    return (
      <div className="p-6 text-center">
        <div className="text-gray-300 text-sm mb-3">
          No subtasks found for this task.
        </div>
      </div>
    );
  }

  return (
    <div className="max-h-[518px] overflow-y-auto pr-2">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {subtasks.map((subtask) => (
          <Card 
            key={subtask.id} 
            className={`${getCardGradient(subtask.status)} border-2 border-white/20 hover:border-primary/40 transition-all duration-300 hover:shadow-lg hover:shadow-primary/10 backdrop-blur-sm transform hover:scale-105`}
          >
            <CardContent className="p-3">
              <div className="flex flex-col space-y-2">
                {/* Action Buttons - Top Right */}
                <div className="flex items-center justify-between mb-1">
                  <Badge 
                    variant="outline" 
                    className={`cursor-pointer transition-all duration-200 flex items-center gap-1 px-2 py-1 text-xs ${getStatusColor(subtask.status)}`}
                    onClick={() => onStatusChange(subtask.id, getNextStatus(subtask.status) as 'To Do' | 'In Progress' | 'Done')}
                  >
                    {getStatusIcon(subtask.status)}
                    {subtask.status}
                  </Badge>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(subtask);
                      }}
                      disabled={isRestrictedUser}
                      className={`h-6 w-6 p-0 ${
                        isRestrictedUser 
                          ? "text-gray-500 cursor-not-allowed hover:text-gray-500 hover:bg-transparent"
                          : "text-blue-300 hover:text-blue-200 hover:bg-blue-500/20"
                      }`}
                    >
                      <Edit2 className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(subtask.id);
                      }}
                      disabled={isRestrictedUser}
                      className={`h-6 w-6 p-0 ${
                        isRestrictedUser 
                          ? "text-gray-500 cursor-not-allowed hover:text-gray-500 hover:bg-transparent"
                          : "text-red-300 hover:text-red-200 hover:bg-red-500/20"
                      }`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>

                {/* Subtask Name */}
                <div className="text-center">
                  <h3 className="text-white font-medium text-sm leading-tight line-clamp-2">
                    {subtask.subtask_name}
                  </h3>
                </div>

                {/* Assigned Resource */}
                <div className="flex items-center justify-center gap-1 bg-primary/10 px-2 py-1 rounded-full border border-primary/20">
                  <User className="h-3 w-3 text-primary" />
                  <span className="text-gray-200 text-xs font-medium">
                    {subtask.name && subtask.role ? `${subtask.name}` : 
                     subtask.name ? subtask.name : 
                     subtask.role ? subtask.role : 
                     'Unassigned'}
                  </span>
                </div>

                {/* Role (if different from name) */}
                {subtask.name && subtask.role && (
                  <div className="text-center">
                    <span className="text-gray-400 text-xs">
                      {subtask.role}
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default SubtasksSummaryView;
